import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2 } from "lucide-react";

const LOGIN_BG = "https://images.unsplash.com/photo-1531972111231-7482a960e109?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnb3Zlcm5tZW50JTIwYnVpbGRpbmclMjBpbnRlcmlvciUyMGxpZ2h0JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4Mjc3MjA4NXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-2 text-navy mb-8">
            <ShieldCheck className="h-6 w-6" strokeWidth={1.5} />
            <span className="font-heading font-extrabold text-lg tracking-tight">SG CORE</span>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-navy">Plateforme de Coordination</h1>
          <p className="text-sm text-muted-foreground mt-2 mb-8">
            SG PODC
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Adresse email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="sg@mci.td" required data-testid="login-email" className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required data-testid="login-password" className="rounded-sm" />
            </div>
            {error && <p className="text-sm text-danger" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full rounded-sm bg-navy hover:bg-navy-dark text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
            </Button>
          </form>

          <p className="text-xs text-muted-foreground mt-6 border-t border-border pt-4">
            Accès réservé. Confidentialité par défaut — charte à 4 niveaux appliquée.
          </p>
        </div>
      </div>

      {/* Right: institutional image */}
      <div className="hidden lg:block relative">
        <img src={LOGIN_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy/80" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <div className="h-1 w-16 bg-gold mb-6" />
          <h2 className="font-heading text-3xl font-bold leading-tight max-w-md">
            26 agents IA, un seul point d'entrée.
          </h2>
          <p className="text-slate-300 mt-3 max-w-md text-sm leading-relaxed">
            SG CORE qualifie, route et consolide. PARAFIX, bras droit du SG, vous accompagne
            de la saisine à la décision — avec rigueur et traçabilité.
          </p>
        </div>
      </div>
    </div>
  );
}
